import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { documentAPI } from '../api/api';
import { DAVA_TURU_LABELS } from '../utils/constants';
import { formatDateForDisplay } from '../utils/dateUtils';
import { downloadBlob } from '../utils/fileDownload';
import './MatbuDilekceler.css';
import { onayla } from '../utils/onayla';

const formatFileSize = (bytes) => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const getFileIcon = (fileName = '', contentType = '') => {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf' || contentType.includes('pdf')) return '📕';
  if (['doc', 'docx'].includes(ext) || contentType.includes('word')) return '📘';
  if (['xls', 'xlsx'].includes(ext) || contentType.includes('sheet') || contentType.includes('excel')) return '📗';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext) || contentType.includes('image')) return '🖼️';
  if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
  return '📄';
};

const getDocId = (doc) => doc.Id ?? doc.id;

const MatbuDilekceler = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [evrakTerm, setEvrakTerm] = useState('');
  const [evrakResults, setEvrakResults] = useState([]);
  const [evrakLoading, setEvrakLoading] = useState(false);
  const [evrakSearched, setEvrakSearched] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchBusy, setBatchBusy] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    const q = evrakTerm.trim();
    if (q.length < 2) {
      setEvrakResults([]);
      setEvrakLoading(false);
      setEvrakSearched(false);
      return undefined;
    }

    let cancelled = false;
    setEvrakLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await documentAPI.searchEvrak(q);
        if (!cancelled) {
          setEvrakResults(Array.isArray(data) ? data : []);
          setEvrakSearched(true);
        }
      } catch (error) {
        console.error('Evrak arama hatası:', error);
        if (!cancelled) {
          setEvrakResults([]);
          setEvrakSearched(true);
        }
      } finally {
        if (!cancelled) setEvrakLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [evrakTerm]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const data = await documentAPI.getMatbuDilekceler();
      const list = Array.isArray(data) ? data : [];
      // Alfabetik sıralama (Türkçe)
      list.sort((a, b) =>
        (a.FileName ?? a.fileName ?? '').localeCompare(b.FileName ?? b.fileName ?? '', 'tr', { sensitivity: 'base' })
      );
      setDocuments(list);
      setSelectedIds([]);
    } catch (error) {
      console.error('Matbu dilekçeler yüklenirken hata:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = useMemo(() => {
    const term = searchTerm.trim().toLocaleLowerCase('tr-TR');
    if (!term) return documents;
    return documents.filter((d) => {
      const name = (d.FileName ?? d.fileName ?? '').toLocaleLowerCase('tr-TR');
      const desc = (d.Description ?? d.description ?? '').toLocaleLowerCase('tr-TR');
      return name.includes(term) || desc.includes(term);
    });
  }, [documents, searchTerm]);

  const visibleIds = useMemo(() => filteredDocuments.map(getDocId), [filteredDocuments]);
  const selectedVisibleIds = useMemo(
    () => selectedIds.filter((id) => visibleIds.includes(id)),
    [selectedIds, visibleIds]
  );
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleIds.length === visibleIds.length;
  const someVisibleSelected = selectedVisibleIds.length > 0 && !allVisibleSelected;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const clearSelection = () => setSelectedIds([]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...Array.from(e.target.files)]);
      e.target.value = '';
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setSelectedFiles([]);
    setDescription('');
    setShowUploadForm(false);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Lütfen en az bir dosya seçin!');
      return;
    }
    setUploading(true);
    let success = 0;
    let failed = 0;
    for (const file of selectedFiles) {
      try {
        await documentAPI.uploadMatbu(file, description || null);
        success++;
      } catch (error) {
        console.error('Matbu dilekçe yüklenirken hata:', error);
        failed++;
      }
    }
    setUploading(false);
    if (success > 0) {
      resetForm();
      await loadDocuments();
    }
    if (failed > 0) {
      alert(`${success} dilekçe yüklendi, ${failed} tanesi yüklenemedi.`);
    }
  };

  const handleDelete = async (documentId) => {
    if (!await onayla('Bu matbu dilekçeyi silmek istediğinize emin misiniz?')) return;
    try {
      await documentAPI.delete(documentId);
      await loadDocuments();
    } catch (error) {
      console.error('Silme hatası:', error);
      alert('Dilekçe silinirken hata oluştu.');
    }
  };

  const downloadOne = async (documentId, fileName) => {
    const result = await documentAPI.download(documentId);
    const blob = result?.blob ?? result;
    await downloadBlob(blob, result?.fileName || fileName || 'dilekce', result?.contentType || null);
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      await downloadOne(documentId, fileName);
    } catch (error) {
      console.error('İndirme hatası:', error);
      alert('Dosya indirilirken hata oluştu.');
    }
  };

  const handlePreview = async (documentId) => {
    try {
      const result = await documentAPI.download(documentId);
      const blob = result?.blob ?? result;
      const mime = result?.contentType || blob?.type;
      const typed = mime && blob.type !== mime ? new Blob([blob], { type: mime }) : blob;
      const url = window.URL.createObjectURL(typed);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Önizleme hatası:', error);
      alert('Dosya önizlenemedi.');
    }
  };

  const handleBatchDownload = async () => {
    const docs = filteredDocuments.filter((d) => selectedIds.includes(getDocId(d)));
    if (docs.length === 0) return;
    setBatchBusy(true);
    let failed = 0;
    for (const d of docs) {
      try {
        await downloadOne(getDocId(d), d.FileName ?? d.fileName);
        // tarayıcının birden fazla indirmeyi engellememesi için küçük bekleme
        await new Promise((r) => setTimeout(r, 350));
      } catch (error) {
        console.error('Toplu indirme hatası:', error);
        failed++;
      }
    }
    setBatchBusy(false);
    if (failed > 0) alert(`${docs.length - failed} dosya indirildi, ${failed} tanesi indirilemedi.`);
  };

  const handleBatchDelete = async () => {
    const ids = [...selectedVisibleIds];
    if (ids.length === 0) return;
    if (!await onayla(`${ids.length} matbu dilekçeyi silmek istediğinize emin misiniz?`)) return;
    setBatchBusy(true);
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        await documentAPI.delete(id);
        ok++;
      } catch (error) {
        console.error('Toplu silme hatası:', error);
        fail++;
      }
    }
    setBatchBusy(false);
    await loadDocuments();
    if (fail > 0) alert(`${ok} dilekçe silindi, ${fail} tanesi silinemedi.`);
  };

  return (
    <div className="app-content matbu-dilekceler-page">
      <div className="page-header">
        <div>
          <h1>Matbu Dilekçeler</h1>
          <p className="page-subtitle">Sık kullanılan şablon dilekçelerinizi buraya yükleyin. Liste alfabetik sıralıdır.</p>
        </div>
        <div className="page-header-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowUploadForm((v) => !v)}
          >
            {showUploadForm ? '✕ Kapat' : '+ Yeni Dilekçe Yükle'}
          </button>
        </div>
      </div>

      {showUploadForm && (
        <div className="matbu-upload-card">
          <div
            className={`matbu-dropzone ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('matbu-file-input')?.click()}
          >
            <div className="matbu-dropzone-icon">📤</div>
            <div className="matbu-dropzone-title">Dosyaları buraya sürükleyin</div>
            <div className="matbu-dropzone-sub">veya tıklayarak seçin (birden fazla dosya seçebilirsiniz)</div>
            <input
              id="matbu-file-input"
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {selectedFiles.length > 0 && (
            <ul className="matbu-selected-list">
              {selectedFiles.map((file, index) => (
                <li key={index} className="matbu-selected-item">
                  <span className="matbu-selected-icon">{getFileIcon(file.name, file.type)}</span>
                  <span className="matbu-selected-name">{file.name}</span>
                  <span className="matbu-selected-size">{formatFileSize(file.size)}</span>
                  <button
                    type="button"
                    className="matbu-selected-remove"
                    onClick={() => removeSelectedFile(index)}
                    title="Kaldır"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="matbu-upload-desc">
            <label>Açıklama (opsiyonel)</label>
            <input
              type="text"
              className="form-control"
              placeholder="Örn: İtiraz dilekçesi şablonu"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="matbu-upload-actions">
            <button className="btn btn-secondary" onClick={resetForm} disabled={uploading}>
              İptal
            </button>
            <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || selectedFiles.length === 0}>
              {uploading ? 'Yükleniyor...' : `Yükle${selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ''}`}
            </button>
          </div>
        </div>
      )}

      <div className="matbu-toolbar">
        <div className="matbu-search-row">
          <div className="matbu-search-wrap">
            <span className="matbu-search-icon">🔍</span>
            <input
              type="text"
              className="form-control matbu-search"
              placeholder="Dilekçe adına göre ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Matbu dilekçe ara"
            />
          </div>
          <div className="matbu-search-wrap">
            <span className="matbu-search-icon">📁</span>
            <input
              type="text"
              className="form-control matbu-search"
              placeholder="Dosya evraklarında ara... (ör. dava dilekçesi)"
              value={evrakTerm}
              onChange={(e) => setEvrakTerm(e.target.value)}
              aria-label="Dosya evraklarında ara"
            />
          </div>
        </div>
        <div className="matbu-count">{filteredDocuments.length} dilekçe</div>
      </div>

      {evrakTerm.trim().length > 0 && (
        <div className="evrak-search-panel">
          <div className="evrak-search-head">
            <div>
              <div className="evrak-search-title">Dosya evrakları</div>
              <div className="evrak-search-sub">Tüm dosyalardaki belgeler arasında arama</div>
            </div>
            <div className="matbu-count">
              {evrakTerm.trim().length < 2
                ? 'En az 2 karakter yazın'
                : evrakLoading
                  ? 'Aranıyor...'
                  : `${evrakResults.length} sonuç`}
            </div>
          </div>

          {evrakTerm.trim().length < 2 ? null : (!evrakSearched || (evrakLoading && evrakResults.length === 0)) ? (
            <div className="matbu-empty evrak-search-empty">Evraklar aranıyor...</div>
          ) : evrakResults.length === 0 ? (
            <div className="matbu-empty evrak-search-empty">
              <div className="matbu-empty-title">Eşleşen evrak bulunamadı</div>
              <div className="matbu-empty-sub">Belge adı veya açıklamasında bu ifadeyi içeren kayıt yok.</div>
            </div>
          ) : (
            <div className="matbu-list evrak-search-list">
              <div className="evrak-list-head">
                <span className="evrak-col-name">Evrak</span>
                <span className="evrak-col-foy">Föy No</span>
                <span className="evrak-col-muvekkil">Müvekkil</span>
                <span className="evrak-col-tur">Dava Türü</span>
                <span className="evrak-col-actions">İşlemler</span>
              </div>
              {evrakResults.map((doc) => {
                const docId = getDocId(doc);
                const fileName = doc.FileName ?? doc.fileName ?? 'evrak';
                const desc = doc.Description ?? doc.description ?? '';
                const caseFileId = doc.CaseFileId ?? doc.caseFileId;
                const isOzel = !!(doc.CaseFileIsOzel ?? doc.caseFileIsOzel);
                const foyNo = doc.CaseFileFoyNo ?? doc.caseFileFoyNo ?? '-';
                const muvekkil = doc.CaseFileMuvekkil ?? doc.caseFileMuvekkil ?? '-';
                const davaTuruVal = doc.CaseFileDavaTuru ?? doc.caseFileDavaTuru;
                const davaTuruLabel = DAVA_TURU_LABELS[davaTuruVal] || '-';
                const fileHref = caseFileId
                  ? (isOzel ? `/ozel-dosyalar/${caseFileId}` : `/case-files/${caseFileId}`)
                  : null;
                return (
                  <div className="evrak-row" key={docId}>
                    <div className="evrak-col-name matbu-name-cell">
                      <span className="matbu-row-icon">{getFileIcon(fileName, doc.ContentType ?? doc.contentType ?? '')}</span>
                      <div className="matbu-name-text">
                        <div className="matbu-row-name" title={fileName}>{fileName}</div>
                        {desc && <div className="matbu-row-sub" title={desc}>{desc}</div>}
                      </div>
                    </div>
                    <span className="evrak-col-foy">
                      {fileHref ? (
                        <Link to={fileHref} className="evrak-file-link" title="Dosyayı aç">
                          {foyNo}
                        </Link>
                      ) : foyNo}
                    </span>
                    <span className="evrak-col-muvekkil" title={muvekkil}>{muvekkil}</span>
                    <span className="evrak-col-tur" title={davaTuruLabel}>{davaTuruLabel}</span>
                    <div className="evrak-col-actions matbu-row-actions">
                      <button className="btn btn-sm btn-secondary" onClick={() => handlePreview(docId)} title="Önizle">👁️</button>
                      <button className="btn btn-sm btn-primary" onClick={() => handleDownload(docId, fileName)} title="İndir">⬇️</button>
                      {fileHref && (
                        <Link to={fileHref} className="btn btn-sm btn-secondary" title="Dosyaya git">📂</Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedVisibleIds.length > 0 && (
        <div className="matbu-batch-bar">
          <span className="matbu-batch-info">{selectedVisibleIds.length} dilekçe seçildi</span>
          <div className="matbu-batch-actions">
            <button className="btn btn-sm btn-primary" onClick={handleBatchDownload} disabled={batchBusy}>
              ⬇️ Seçilenleri İndir
            </button>
            <button className="btn btn-sm btn-danger" onClick={handleBatchDelete} disabled={batchBusy}>
              🗑️ Seçilenleri Sil
            </button>
            <button className="btn btn-sm btn-secondary" onClick={clearSelection} disabled={batchBusy}>
              Seçimi Temizle
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="matbu-empty">Yükleniyor...</div>
      ) : filteredDocuments.length === 0 ? (
        <div className="matbu-empty">
          <div className="matbu-empty-icon">🗂️</div>
          <div className="matbu-empty-title">
            {searchTerm ? 'Eşleşen dilekçe bulunamadı' : 'Henüz matbu dilekçe eklenmedi'}
          </div>
          {!searchTerm && (
            <div className="matbu-empty-sub">Yukarıdaki “Yeni Dilekçe Yükle” butonuyla ekleyebilirsiniz.</div>
          )}
        </div>
      ) : (
        <div className="matbu-list">
          <div className="matbu-list-head">
            <label className="matbu-check-wrap" title="Tümünü seç">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                onChange={toggleSelectAll}
              />
            </label>
            <span className="matbu-col-name">Dilekçe Adı</span>
            <span className="matbu-col-size">Boyut</span>
            <span className="matbu-col-date">Tarih</span>
            <span className="matbu-col-actions">İşlemler</span>
          </div>

          {filteredDocuments.map((doc) => {
            const docId = getDocId(doc);
            const fileName = doc.FileName ?? doc.fileName ?? 'dilekce';
            const desc = doc.Description ?? doc.description ?? '';
            const size = doc.FileSize ?? doc.fileSize ?? 0;
            const uploader = doc.UploadedByUserName ?? doc.uploadedByUserName ?? '';
            const created = doc.CreatedDate ?? doc.createdDate;
            const isSelected = selectedIds.includes(docId);
            return (
              <div
                className={`matbu-row ${isSelected ? 'selected' : ''}`}
                key={docId}
                onDoubleClick={() => handlePreview(docId)}
                title="Önizlemek için çift tıklayın"
              >
                <label className="matbu-check-wrap" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(docId)}
                  />
                </label>
                <div className="matbu-col-name matbu-name-cell">
                  <span className="matbu-row-icon">{getFileIcon(fileName, doc.ContentType ?? doc.contentType ?? '')}</span>
                  <div className="matbu-name-text">
                    <div className="matbu-row-name" title={fileName}>{fileName}</div>
                    {(desc || uploader) && (
                      <div className="matbu-row-sub" title={desc}>
                        {desc}
                        {desc && uploader ? ' • ' : ''}
                        {uploader}
                      </div>
                    )}
                  </div>
                </div>
                <span className="matbu-col-size">{formatFileSize(size)}</span>
                <span className="matbu-col-date">{created ? formatDateForDisplay(created) : '-'}</span>
                <div
                  className="matbu-col-actions matbu-row-actions"
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                >
                  <button className="btn btn-sm btn-secondary" onClick={() => handlePreview(docId)} title="Önizle">👁️</button>
                  <button className="btn btn-sm btn-primary" onClick={() => handleDownload(docId, fileName)} title="İndir">⬇️</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(docId)} title="Sil">🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MatbuDilekceler;
